import React, { useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import Breadcrumb from "./HelpCenter/Breadcrumb";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const HelpCenter = () => {
  const [selectedTopic, setSelectedTopic] = useState("");

  const topics = [
    "Billing & Payments",
    "Technical Support",
    "Account Issues",
    "Sales Inquiry",
    "Other",
  ];

  const initialValues = {
    email: "",
    confirmEmail: "",
    subscriptionStatus: "",
    device: "",
    issueType: "",
    subject: "",
    description: "",
    attachment: null,
  };

  const validationSchema = Yup.object({
    email: Yup.string().email("Invalid email").required("Required"),
    confirmEmail: Yup.string()
      .oneOf([Yup.ref("email"), null], "Emails must match")
      .required("Required"),
    subscriptionStatus: Yup.string().required("Required"),
    device: Yup.string().required("Required"),
    issueType: Yup.string().required("Required"),
    subject: Yup.string().required("Required"),
    description: Yup.string().required("Required"),
  });

  const handleSubmit = (values) => {
    console.log(values);
  };

  return (
    <div className="min-h-screen bg-dry text-text pt-32 pb-16">
      {/* Breadcrumb */}
      <div className="max-w-5xl mx-auto px-4">
        <Breadcrumb />
      </div>

      {/* Header Banner */}
      <div
        className="relative w-screen overflow-hidden py-24 px-0 text-center mb-10"
        style={{
          backgroundColor: "#FDE8F0",
          backgroundImage: 'url("/images/pattern/bg-15.svg")',
          backgroundRepeat: "repeat",
          backgroundSize: "auto",
        }}
      >
        <div className="relative z-10">
          <h1 className="text-3xl md:text-4xl font-semibold mb-4 capitalize font-serif text-dry">
            Contact our friendly <br /> support team
          </h1>
        </div>

        <div className="absolute inset-0 flex justify-center items-center">
          <div className="w-full h-full relative">
            <img
              src="/images/galery/blog_img_05.jpg"
              alt="Avatar 1"
              className="hidden xl:block absolute top-8 left-10 w-24 h-24 object-cover rounded-full shadow-lg"
            />
            <img
              src="/images/galery/blog_img_06.jpg"
              alt="Avatar 2"
              className="hidden xl:block absolute top-36 right-10 w-24 h-24 object-cover rounded-full shadow-lg"
            />
            <img
              src="/images/galery/blog_img_04.jpg"
              alt="Avatar 3"
              className="hidden xl:block absolute bottom-10 left-1/4 w-32 h-32 object-cover rounded-full shadow-lg"
            />
          </div>
        </div>
      </div>

      {/* Topic Selector */}
      <div className="max-w-xl mx-auto text-center p-4">
        <p className="text-sm">
          Answer the questions below so our support team has all the information
          we need. The more we know, the faster we can help!
        </p>
      </div>
      <div className="max-w-xl mx-auto px-4">
        <label className="block text-sm font-bold mb-3">
          Please select from the following
        </label>
        <div className="relative">
          <select
            className="w-full h-12 bg-black border border-border rounded-md px-4 text-text appearance-none focus:ring-2 focus:ring-red-400 text-sm"
            value={selectedTopic}
            onChange={(e) => setSelectedTopic(e.target.value)}
          >
            <option value="-">-</option>
            {topics.map((topic, idx) => (
              <option key={idx} value={topic}>
                {topic}
              </option>
            ))}
          </select>
          <ChevronDownIcon className="w-5 h-5 absolute top-3 right-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Show form only if topic is valid */}
      {selectedTopic !== "" && selectedTopic !== "-" && (
        <div className="max-w-xl mx-auto px-4 pt-8">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue }) => (
              <Form className="space-y-6">
                <div>
                  <label className="block mb-1 text-sm font-semibold">
                    Your email address
                  </label>
                  <Field
                    name="email"
                    type="email"
                    className="w-full h-12 bg-black border border-border rounded-md px-4 text-white"
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="text-red-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm font-semibold">
                    Please confirm the email address which is linked with your
                    Lokalai account for verification
                  </label>
                  <Field
                    name="confirmEmail"
                    type="email"
                    className="w-full h-12 bg-black border border-border rounded-md px-4 text-white"
                  />
                  <ErrorMessage
                    name="confirmEmail"
                    component="div"
                    className="text-red-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm font-semibold">
                    Subscription Status
                  </label>
                  <Field
                    as="select"
                    name="subscriptionStatus"
                    className="w-full h-12 bg-black border border-border rounded-md px-4 text-white"
                  >
                    <option value="">-</option>
                    <option>Trial</option>
                    <option>Active</option>
                    <option>Cancelled</option>
                  </Field>
                  <ErrorMessage
                    name="subscriptionStatus"
                    component="div"
                    className="text-red-500 text-sm"
                  />
                  <label className="text-[10px] text-textGray">
                    E.g On a 14 day trial or an existing customer or have
                    previously cancelled your account
                  </label>
                </div>

                <div>
                  <label className="block mb-1 text-sm font-semibold">
                    Device
                  </label>
                  <Field
                    as="select"
                    name="device"
                    className="w-full h-12 bg-black border border-border rounded-md px-4 text-white"
                  >
                    <option value="">-</option>
                    <option>iOS</option>
                    <option>Android</option>
                    <option>Web/Desktop</option>
                  </Field>
                  <ErrorMessage
                    name="device"
                    component="div"
                    className="text-red-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm font-semibold">
                    Issue Type
                  </label>
                  <Field
                    as="select"
                    name="issueType"
                    className="w-full h-12 bg-black border border-border rounded-md px-4 text-white"
                  >
                    <option value="">-</option>
                    <option>Technical</option>
                    <option>Billing</option>
                    <option>Account</option>
                    <option>Sales</option>
                  </Field>
                  <ErrorMessage
                    name="issueType"
                    component="div"
                    className="text-red-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm font-semibold">
                    Subject
                  </label>
                  <Field
                    name="subject"
                    type="text"
                    className="w-full h-12 bg-black border border-border rounded-md px-4 text-white"
                  />
                  <ErrorMessage
                    name="subject"
                    component="div"
                    className="text-red-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block mb-1">Description</label>
                  <Field name="description">
                    {({ field, form }) => (
                      <div className="rounded-md border-0 border-border pb-10">
                        <ReactQuill
                          value={field.value}
                          onChange={(val) =>
                            form.setFieldValue("description", val)
                          }
                          theme="snow"
                          modules={{
                            toolbar: [
                              [{ header: [1, 2, false] }],
                              ["bold", "italic", "underline", "strike"],
                              ["link", "image"],
                              [{ list: "ordered" }, { list: "bullet" }],
                              ["clean"],
                            ],
                          }}
                          className="bg-dry text-white"
                          style={{
                            height: "200px", // increase editor height here
                            border: "none",
                            borderColor: "red !important", // remove internal default border
                            color: "gray",
                          }}
                        />
                      </div>
                    )}
                  </Field>
                  <ErrorMessage
                    name="description"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* <div>
                  <label className="block mb-1">Attachment (optional)</label>
                  <input
                    name="attachment"
                    type="file"
                    onChange={(event) =>
                      setFieldValue("attachment", event.target.files[0])
                    }
                    className="w-full h-12 bg-black border border-border rounded-md px-4 text-white"
                  />
                </div> */}

                <div className="text-center">
                  <button
                    type="submit"
                    className="bg-redlight text-white px-10 py-3 rounded-full text-sm hover:text-reddark"
                  >
                    Submit
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default HelpCenter;
